import i18n from "@/i18n";
import { enGB, nb } from "date-fns/locale";
import type { Locale } from "date-fns";
import { countryToNumberLocale } from "@/lib/countries";

// Number-formatting locale is driven by the user's *profile country*, not
// the UI language. A signed-in Norwegian reading the app in English still
// sees `7 394 584 kr`. Date formatting continues to follow UI language.
let _profileCountry: string | null = null;

// Tiny subscription store so React components can re-render when the
// formatting country changes, without invalidating (and refetching) every
// query in the cache. Consumed via `useFormattingCountry()` below.
const _listeners = new Set<() => void>();

/**
 * Set (or clear) the profile country used to resolve the number-formatting
 * locale. Called from a root-level effect when the profile query resolves,
 * and again whenever the user changes their country on the profile page.
 * Safe to call with null / undefined — formatters fall back to UI language.
 */
export function setFormattingCountry(country: string | null | undefined) {
  const next = country ?? null;
  if (_profileCountry === next) return;
  _profileCountry = next;
  _listeners.forEach((l) => l());
}

/**
 * Subscribe to changes in the formatting country. Returned unsubscribe
 * removes the listener. Used by `useFormattingCountry` — most callers
 * should use that hook instead.
 */
export function subscribeFormattingCountry(listener: () => void): () => void {
  _listeners.add(listener);
  return () => {
    _listeners.delete(listener);
  };
}

/** Current formatting country, or null when unset. Snapshot for React. */
export function getFormattingCountry(): string | null {
  return _profileCountry;
}

function uiLanguageLocale(): string {
  return i18n.language === "nb" ? "nb-NO" : "en-GB";
}

function numberLocale(): string {
  return countryToNumberLocale(_profileCountry) ?? uiLanguageLocale();
}

// Kept for date/month formatting only — follows UI language.
function dateIntlLocale(): string {
  return uiLanguageLocale();
}

export function dateFnsLocale(): Locale {
  return i18n.language === "nb" ? nb : enGB;
}

/**
 * Localized full date.
 * - Norwegian: "DD.MMM.YYYY" (e.g. "14.jun.2026")
 * - English:   "14 Jun 2026"
 */
export function formatDate(date: Date | string | null | undefined): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "";
  if (i18n.language === "nb") {
    const day = String(d.getDate()).padStart(2, "0");
    const month = new Intl.DateTimeFormat("nb-NO", { month: "short" }).format(d).replace(".", "");
    const year = d.getFullYear();
    return `${day}.${month}.${year}`;
  }
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(d);
}

const CURRENCY_SYMBOL: Record<string, string> = {
  NOK: "kr",
  SEK: "kr",
  DKK: "kr",
  EUR: "€",
  USD: "$",
  GBP: "£",
};

export function formatMoney(
  value: number | null | undefined,
  currency: string = "NOK",
  opts: { decimals?: boolean } = {},
): string {
  if (value == null || isNaN(value)) return "—";
  // Rule A: lists/cards/totals show whole units.
  // Rule B: where decimals are allowed, show up to 2 but never force
  // trailing zeros — 99.00 -> "99", 99.90 -> "99,90".
  const hasFraction = Math.abs(value - Math.trunc(value)) > 1e-9;
  const useDecimals = !!opts.decimals && hasFraction;
  const fmt = new Intl.NumberFormat(numberLocale(), {
    style: "decimal",
    minimumFractionDigits: useDecimals ? 2 : 0,
    maximumFractionDigits: useDecimals ? 2 : 0,
  });
  const symbol = CURRENCY_SYMBOL[currency.toUpperCase()] ?? currency;
  return `${fmt.format(value)} ${symbol}`;
}

export function formatNok(
  value: number | null | undefined,
  opts: { decimals?: boolean } = {},
): string {
  return formatMoney(value, "NOK", opts);
}

/**
 * Country-aware whole-number formatter (no currency suffix).
 * Used where the currency is implied by context (e.g. the salary list, where
 * repeating "kr" on every row would be noise).
 */
export function formatNumber(
  value: number | null | undefined,
  opts: { decimals?: boolean } = {},
): string {
  if (value == null || isNaN(value)) return "—";
  const hasFraction = Math.abs(value - Math.trunc(value)) > 1e-9;
  const useDecimals = !!opts.decimals && hasFraction;
  return new Intl.NumberFormat(numberLocale(), {
    style: "decimal",
    minimumFractionDigits: useDecimals ? 2 : 0,
    maximumFractionDigits: useDecimals ? 2 : 0,
  }).format(value);
}

/**
 * Convert a stored money value into the string shown inside an edit input.
 * - Uses the decimal separator of the resolved number locale (comma for
 *   Norway, dot for the UK).
 * - Strips trailing zero decimals: 123.00 -> "123", 123.30 -> "123,3".
 * - No thousand grouping (would cause cursor jumps while typing).
 */
export function formatMoneyEdit(value: number | string | null | undefined): string {
  if (value == null || value === "") return "";
  const n = typeof value === "string" ? Number(value) : value;
  if (!isFinite(n)) return "";
  const rounded = Math.round(n * 100) / 100;
  const sign = rounded < 0 ? "-" : "";
  const abs = Math.abs(rounded);
  const intPart = Math.trunc(abs).toString();
  const frac = Math.round((abs - Math.trunc(abs)) * 100);
  if (frac === 0) return `${sign}${intPart}`;
  const fracStr = String(frac).padStart(2, "0");
  const parts = new Intl.NumberFormat(numberLocale()).formatToParts(1.1);
  const sep = parts.find((p) => p.type === "decimal")?.value ?? ".";
  return `${sign}${intPart}${sep}${fracStr}`;
}

/**
 * Signed money delta for use next to a base amount. No currency suffix,
 * no decimals, always shows a sign for non-zero values (`+30 000`, `−30 000`
 * with the locale minus sign). Returns null when the delta rounds to zero
 * — callers should render nothing rather than `+0`.
 */
export function formatSignedDelta(value: number | null | undefined): string | null {
  if (value == null || !isFinite(value)) return null;
  const rounded = Math.round(value);
  if (rounded === 0) return null;
  const abs = Math.abs(rounded);
  const fmt = new Intl.NumberFormat(numberLocale(), {
    style: "decimal",
    maximumFractionDigits: 0,
  });
  // Use the locale's own minus sign (Intl emits it in negative formatting).
  const negParts = new Intl.NumberFormat(numberLocale(), { signDisplay: "always" }).formatToParts(
    -1,
  );
  const minus = negParts.find((p) => p.type === "minusSign")?.value ?? "−";
  return rounded > 0 ? `+${fmt.format(abs)}` : `${minus}${fmt.format(abs)}`;
}

export function formatPercent(
  value: number | null | undefined,
  opts: { signed?: boolean } = {},
): string {
  if (value == null || isNaN(value)) return "—";
  const rounded = Math.round(value * 10) / 10;
  const sign = opts.signed && rounded > 0 ? "+" : "";
  return `${sign}${rounded.toFixed(1)}%`;
}

export function monthlyEquivalent(
  amount: number,
  cycle: "monthly" | "quarterly" | "yearly",
): number {
  switch (cycle) {
    case "monthly":
      return amount;
    case "quarterly":
      return amount / 3;
    case "yearly":
      return amount / 12;
  }
}

export function formatMonth(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const s = new Intl.DateTimeFormat(dateIntlLocale(), { month: "long", year: "numeric" }).format(d);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function formatDay(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat(dateIntlLocale(), { day: "numeric", month: "short" }).format(d);
}
